import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeePerformance, setMonth, setAmType, setSearch, setSortConfig, selectEmployeePerformanceRows, selectEmployeePerformanceTotalSummary, selectEmployeePerformancePagination, selectEmployeePerformanceLoading, selectEmployeePerformanceMonth, selectEmployeePerformanceAmType, selectEmployeePerformanceSearch, selectEmployeePerformancePage, selectEmployeePerformanceSortBy, selectEmployeePerformanceSortOrder, } from "../../redux/employeePerformanceSlice";
import PageContainer from "../../common_components/PageContainer";
import { Dropdown, MonthPicker } from "../../common_components/FormInputs";
import TableToolbar from "../../common_components/TableToolbar";
import CommonTable from "../../common_components/CommonTable";
import CommonLoader from "../../common_components/CommonLoader";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import PerformanceProgressBar from "./PerformanceProgressBar";
import { SUMMARY_ROW_POSITIONS, EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import { truncateName } from "../../helper/truncateName";
import { ApiCall } from "../../helper/axios";
import ExportLoadingButton from "./ExportLoadingButton";
import exportEmployeePerformance from "./exportEmployeePerformance";
import { Navigate, useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routes.constants";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
// ═══════════════ CONSTANTS ═══════════════
const AM_TYPE_OPTIONS = [
    { value: "all", label: "All" },
    { value: "paid_media", label: "Paid Media" },
    { value: "affiliate", label: "Affiliate" },
];
const BADGE_LABELS = {
    paid_media: "Paid Media",
    affiliate: "Affiliate",
    "affiliate,paid_media": "Both",
};
const BADGE_COLORS = {
    affiliate: { bg: "#eff8ff", color: "#175cd3" }, // blue
    paid_media: { bg: "#f4f3ff", color: "#6938ef" }, // purple
    "affiliate,paid_media": { bg: "#f0fdf9", color: "#107569" }, // teal — "Both"
};
const columns = [
    { id: "accountManager", label: "Account Manager", width: "200px", align: "left", sortable: true },
    { id: "accounts", label: "Account", width: "160px", align: "center", sortable: true },
    { id: "managementFees", label: "Management Fees", width: "250px", align: "center", sortable: true },
    { id: "commissions", label: "Commissions", width: "180px", align: "center", sortable: true },
    { id: "placements", label: "Placement", width: "240px", align: "center", sortable: true },
    { id: "spendPerformance", label: "Spend Performance", width: "250px", align: "center", sortable: true },
    { id: "revenuePerformance", label: "Revenue Performance", width: "250px", align: "center", sortable: true },
];
// Account Manager stays visible while the metric columns scroll horizontally.
const PINNED_COLUMNS = { left: ["accountManager"], right: [] };
// Value-getters for the CLIENT-SIDE sort path (dataset fully loaded, <=20
// total AMs). Mirrors the backend's SORT_FIELD_GETTERS in
// employeePerformanceController.ts so both paths order rows identically.
const SORT_GETTERS = {
    accountManager: (r) => (r.name || "").toLowerCase(),
    accounts: (r) => r.accounts?.total || 0,
    managementFees: (r) => r.management_fees?.total || 0,
    commissions: (r) => r.commissions?.total || 0,
    placements: (r) => (typeof r.placements === "object" ? r.placements?.total || 0 : 0),
    spendPerformance: (r) => r.spend_performance?.actual || 0,
    revenuePerformance: (r) => r.revenue_performance?.actual || 0,
};
// ═══════════════ HELPERS ═══════════════
// Backend uses "MM/YYYY"; the shared MonthPicker/redux convention elsewhere
// in this app is "YYYY-MM" — converted at the edges so the slice/page stay
// consistent with the rest of the codebase while the API keeps its own format.
const toApiMonth = (yyyyMm) => {
    const [y, m] = yyyyMm.split("-");
    return `${m}/${y}`;
};
const toCalendarDate = (apiMonth) => {
    if (!apiMonth)
        return null;
    const [m, y] = apiMonth.split("/").map(Number);
    if (!m || !y)
        return null;
    return new CalendarDate(y, m, 1);
};
const getDefaultApiMonth = () => {
    const now = today(getLocalTimeZone());
    return `${String(now.month).padStart(2, "0")}/${now.year}`;
};
const money = (v) => (v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDateMMDDYYYY = (dateStr) => {
    if (!dateStr)
        return '';
    const str = String(dateStr);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str))
        return str;
    const d = new Date(str);
    if (isNaN(d.getTime()))
        return str;
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${mm}/${dd}/${yyyy}`;
};
// One type scale for the whole page — every cell, tooltip, and the summary
// row pull from here so headline/secondary/fine-print text stays identical
// in size and weight everywhere it appears, instead of drifting per-cell.
const FONT = {
    headline: { fontFamily: "Inter", fontWeight: 600, fontSize: "16px", color: "#181d27" },
    headlineMuted: { fontFamily: "Inter", fontWeight: 400, fontSize: "16px", color: "#98a2b3" },
    secondary: { fontFamily: "Inter", fontWeight: 500, fontSize: "12px", color: "#667085" },
    fine: { fontFamily: "Inter", fontWeight: 400, fontSize: "11px", color: "#98a2b3" },
};
const renderDropdownValue = (label, value) => (_jsxs("span", { className: "all-Font-Sizes", children: [_jsxs("span", { style: { color: "#101828", fontWeight: 500 }, children: [label, ":"] }), " ", _jsx("span", { style: { color: "#667085" }, children: value })] }));
// ═══════════════ SUB-COMPONENTS ═══════════════
const AmBadge = React.memo(({ type }) => {
    const colors = BADGE_COLORS[type] || BADGE_COLORS.affiliate;
    return (_jsx(Box, { sx: {
            display: "inline-block", px: 1.5, py: 0.5, borderRadius: "16px",
            backgroundColor: colors.bg, ...FONT.secondary, color: colors.color,
        }, children: BADGE_LABELS[type] || type }));
});
// Shared hover-tooltip content: a dark, two-column (name / value) list —
// used for every breakdown popover across the page (accounts, fees,
// commissions, spend, revenue) so they all look and behave identically.
const TOOLTIP_LINE = "1px solid rgba(255,255,255,0.14)";
export const TooltipRows = ({ heading, items }) => {
    const hasValues = items.some((item) => item.value);
    return (
    // Negative margin cancels most of the shared HoverTooltip's 10px 14px padding,
    // so the table sits close to the tooltip edge.
    _jsxs(Box, { sx: {
            m: "-6px -10px",
            minWidth: 180,
            maxHeight: 300,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
        }, children: [heading && (_jsx(Box, { sx: {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: "#0B0F19",
                    p: "6px 10px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: "inherit",
                    borderBottom: TOOLTIP_LINE,
                }, children: heading })), _jsx(Box, { component: "table", sx: { width: "100%", borderCollapse: "collapse" }, children: _jsx("tbody", { children: items.map((item, i) => {
                        const isLast = i === items.length - 1;
                        const rowBorder = isLast ? "none" : TOOLTIP_LINE;
                        return (_jsxs("tr", { children: [_jsxs(Box, { component: "td", sx: { p: "3px 10px", textAlign: "left", verticalAlign: "middle", borderBottom: rowBorder }, children: [_jsx(Box, { sx: { ...FONT.secondary, fontSize: "13px", color: "inherit", lineHeight: 1.25 }, children: item.name }), item.sub && _jsx(Box, { sx: { ...FONT.fine, color: "inherit", opacity: 0.65, lineHeight: 1.2 }, children: item.sub })] }), hasValues && (_jsx(Box, { component: "td", sx: {
                                        p: "3px 10px",
                                        minWidth: 44,
                                        textAlign: "right",
                                        verticalAlign: "middle",
                                        whiteSpace: "nowrap",
                                        borderLeft: TOOLTIP_LINE,
                                        borderBottom: rowBorder,
                                        ...FONT.secondary,
                                        fontSize: "13px",
                                        color: "inherit",
                                        fontWeight: 600,
                                    }, children: item.value }))] }, `${item.name}-${i}`));
                    }) }) })] }));
};
const VarianceBadge = React.memo(({ value }) => {
    const isPos = (value || 0) >= 0;
    return (_jsxs(Box, { sx: {
            backgroundColor: isPos ? "#ecfdf3" : "#fef3f2",
            borderRadius: "16px", px: 1.5, py: 0.35,
            display: "inline-flex", alignItems: "center", gap: 0.5
        }, children: [_jsxs(Typography, { sx: { ...FONT.secondary, color: isPos ? "#027a48" : "#b42318", fontWeight: 600, lineHeight: 1.2 }, children: [Math.abs(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 }), "%"] }), isPos ? (_jsx(TrendingUpIcon, { sx: { fontSize: 16, color: "#12B76A" } })) : (_jsx(TrendingDownIcon, { sx: { fontSize: 16, color: "#F04438" } }))] }));
});
const AccountsCell = React.memo(({ accounts }) => {
    if (!accounts)
        return null;
    const { total, new: newCount, lost, list, newList, lostList, nomgmtFeeList } = accounts;
    const noMgmtCount = nomgmtFeeList?.length || 0;
    // Old portal shows the net count (a lost client is taken off); the API `total` and the sort still use the gross one.
    const netTotal = Math.round((total - lost) * 100) / 100;
    // Grouped child accounts carry 0 credit - they don't count toward the total, so the tooltip leaves them out.
    const creditedList = list?.filter((i) => i.credit > 0);
    return (_jsxs(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", flexDirection: "column" }, children: [_jsx(Box, { sx: {
                    bgcolor: "#f4f7fe",
                    py: 1,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    borderBottom: "1px solid #edf1f7",
                    width: "100%",
                }, children: _jsx(HoverTooltip, { title: creditedList?.length ? _jsx(TooltipRows, { heading: "Credit", items: creditedList.map((i) => ({ name: i.name, value: i.credit })) }) : "No current accounts", children: _jsx(Typography, { sx: {
                            ...FONT.headline,
                            cursor: "default",
                            textAlign: "center",
                            ...(creditedList?.length ? { display: "inline-block", borderBottom: "2px dotted #181d27", pb: "1px", lineHeight: 1.2 } : {}),
                        }, children: netTotal }) }) }), _jsxs(Box, { sx: {
                    flex: 1,
                    py: 1.25,
                    px: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    bgcolor: "#fff",
                }, children: [lost > 0 && (_jsx(HoverTooltip, { title: _jsx(TooltipRows, { heading: "Lost Client", items: lostList?.map((i) => ({ name: i.name, value: i.credit, sub: formatDateMMDDYYYY(i.termination_date) })) || [] }), children: _jsxs(Box, { sx: {
                                backgroundColor: "#fef3f2",
                                borderRadius: "16px",
                                px: 1.25,
                                py: 0.25,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                cursor: "default",
                            }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", backgroundColor: "#F04438", flexShrink: 0 } }), _jsxs(Typography, { sx: { ...FONT.secondary, color: "#b42318", lineHeight: 1.2, fontWeight: 500 }, children: [lost, " Lost"] })] }) })), newCount > 0 && (_jsx(HoverTooltip, { title: _jsx(TooltipRows, { heading: "New Client", items: newList?.map((i) => ({ name: i.name, value: i.credit, sub: formatDateMMDDYYYY(i.start_date) })) || [] }), children: _jsxs(Box, { sx: {
                                backgroundColor: "#ecfdf3",
                                borderRadius: "16px",
                                px: 1.25,
                                py: 0.25,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                cursor: "default",
                            }, children: [_jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", backgroundColor: "#12B76A", flexShrink: 0 } }), _jsxs(Typography, { sx: { ...FONT.secondary, color: "#027a48", lineHeight: 1.2, fontWeight: 500 }, children: [newCount, " New"] })] }) }))] })] }));
});
const ManagementFeesCell = React.memo(({ data, accounts }) => {
    if (!data)
        return null;
    const { total, new: newAmount, lost, list, mtd_goal_percent, avg_mrr } = data;
    const noMgmtCount = accounts?.nomgmtFeeList?.length || 0;
    return (_jsxs(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", flexDirection: "column" }, children: [_jsxs(Box, { sx: {
                    bgcolor: "#f4f7fe",
                    py: 1,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    borderBottom: "1px solid #edf1f7",
                    width: "100%",
                }, children: [_jsx(HoverTooltip, { title: list?.length ? _jsx(TooltipRows, { heading: "Management Fees", items: list.map((i) => ({ name: i.name, value: `$ ${money(i.fee)}` })) }) : "No management fees", children: _jsxs(Typography, { sx: { ...FONT.headline, cursor: "default", textAlign: "center" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(total)] }) }), noMgmtCount > 0 && (_jsx(HoverTooltip, { title: _jsx(TooltipRows, { heading: "$0 Fee Client", items: accounts?.nomgmtFeeList?.map((i) => ({ name: i.name, value: "" })) || [] }), children: _jsx(Box, { sx: {
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                backgroundColor: "#fee4e2",
                                color: "#b42318",
                                fontSize: "12px",
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "default",
                            }, children: _jsx(Box, { component: "span", sx: { lineHeight: 1.2, pb: "1px", borderBottom: "2px dotted #b42318" }, children: noMgmtCount }) }) }))] }), _jsxs(Box, { sx: {
                    flex: 1,
                    py: 1.25,
                    px: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    bgcolor: "#fff",
                }, children: [_jsx(PerformanceProgressBar, { current: total, goal: data.goal, mtdPercent: mtd_goal_percent, goalLabel: `$ ${money(data.goal)}` }), _jsxs(Typography, { className: "ep-small", sx: { ...FONT.secondary, fontSize: "12px", color: "#181d27", mt: 0.5, textAlign: "center" }, children: ["Avg MRR: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#181d27", fontWeight: 600 }, children: money(avg_mrr) })] })] }), _jsxs(Box, { sx: { display: "flex", width: "100%", borderTop: "1px solid #edf1f7" }, children: [_jsx(Box, { sx: {
                            flex: 1,
                            bgcolor: "#ecfdf3",
                            py: 0.75,
                            px: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRight: "1px solid #edf1f7",
                        }, children: _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#027a48", whiteSpace: "nowrap" }, children: ["New: ", _jsx("span", { style: { color: "#027a48", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#027a48", fontWeight: 600 }, children: money(newAmount) })] }) }), _jsx(Box, { sx: {
                            flex: 1,
                            bgcolor: "#fef3f2",
                            py: 0.75,
                            px: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }, children: _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#b42318", whiteSpace: "nowrap" }, children: ["Lost: ", _jsx("span", { style: { color: "#b42318", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#b42318", fontWeight: 600 }, children: money(lost) })] }) })] })] }));
});
const CommissionsCell = React.memo(({ data }) => {
    if (!data)
        return null;
    const { total, list, new: newAmount, change_percent } = data;
    return (_jsxs(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", flexDirection: "column" }, children: [_jsx(Box, { sx: {
                    bgcolor: "#f4f7fe",
                    py: 1,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: "1px solid #edf1f7",
                    width: "100%",
                }, children: _jsx(HoverTooltip, { title: list?.length ? _jsx(TooltipRows, { heading: "Commission", items: list.map((i) => ({ name: i.name, value: `$ ${money(i.commission)}` })) }) : "No commissions", children: _jsxs(Typography, { sx: { ...FONT.headline, cursor: "default", textAlign: "center" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(total)] }) }) }), _jsx(Box, { sx: {
                    flex: 1,
                    py: 1.25,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#fff",
                }, children: _jsx(VarianceBadge, { value: change_percent }) }), _jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    borderTop: "1px solid #edf1f7",
                    py: 0.75,
                    px: 1.5,
                    bgcolor: "#F6FEF9",
                }, children: [_jsx(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#181d27" }, children: "New:" }), _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 600, color: "#181d27" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(newAmount)] })] })] }));
});
const PlacementsCell = React.memo(({ data }) => {
    if (!data || data === "N/A" || typeof data !== "object") {
        return (_jsx(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", alignItems: "center", justifyContent: "center" }, children: _jsx(Typography, { sx: { ...FONT.headlineMuted, px: 1 }, children: "N/A" }) }));
    }
    const { total, total_organic, total_paid, total_seo, goal, mtd_goal_percent, change_percent, list } = data;
    return (_jsxs(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", flexDirection: "column" }, children: [_jsxs(Box, { sx: {
                    bgcolor: "#f4f7fe",
                    py: 1,
                    px: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    borderBottom: "1px solid #edf1f7",
                    width: "100%",
                }, children: [_jsx(HoverTooltip, { title: list?.length ? (_jsx(TooltipRows, { heading: "Placement", items: list.map((item) => ({
                                name: item.name || item.publication || "Placement",
                                value: formatDateMMDDYYYY(item.date),
                            })) })) : ("No placements"), children: _jsxs(Typography, { sx: { ...FONT.headline, cursor: "default", textAlign: "center" }, children: ["TTL: ", total] }) }), change_percent !== undefined && change_percent !== null && (_jsx(VarianceBadge, { value: change_percent }))] }), _jsx(Box, { sx: {
                    flex: 1,
                    py: 1.25,
                    px: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    bgcolor: "#fff",
                }, children: _jsx(PerformanceProgressBar, { current: total, goal: goal, mtdPercent: mtd_goal_percent, goalLabel: String(goal ?? 0) }) }), _jsxs(Box, { sx: { display: "flex", width: "100%", borderTop: "1px solid #edf1f7" }, children: [_jsx(Box, { sx: {
                            flex: 1,
                            bgcolor: "#e8f8f5",
                            py: 0.75,
                            px: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            borderRight: "1px solid #edf1f7",
                        }, children: _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#107569", whiteSpace: "nowrap" }, children: ["Organic: ", _jsx("span", { style: { fontWeight: 600 }, children: total_organic || 0 })] }) }), _jsx(Box, { sx: {
                            flex: 1,
                            bgcolor: "#fff9e6",
                            py: 0.75,
                            px: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            borderRight: "1px solid #edf1f7",
                        }, children: _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#b54708", whiteSpace: "nowrap" }, children: ["Paid: ", _jsx("span", { style: { fontWeight: 600 }, children: total_paid || 0 })] }) }), _jsx(Box, { sx: {
                            flex: 1,
                            bgcolor: "#f4f3ff",
                            py: 0.75,
                            px: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }, children: _jsxs(Typography, { className: "ep-small", sx: { fontSize: "12px", fontWeight: 500, color: "#6938ef", whiteSpace: "nowrap" }, children: ["SEO: ", _jsx("span", { style: { fontWeight: 600 }, children: total_seo || 0 })] }) })] })] }));
});
const PerformanceCell = React.memo(({ data, field }) => {
    if (!data)
        return null;
    const { actual, goal, mtd_goal_percent, list } = data;
    const isSpend = field === "spend";
    return (_jsxs(Box, { sx: { width: "100%", height: "100%", minHeight: 90, display: "flex", flexDirection: "column" }, children: [_jsx(Box, { sx: {
                    bgcolor: "#f4f7fe",
                    py: 1,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom: "1px solid #edf1f7",
                    width: "100%",
                }, children: _jsx(HoverTooltip, { title: list?.length
                        ? _jsx(TooltipRows, { heading: isSpend ? "Spend" : "Revenue", items: list.map((i) => ({ name: i.name, value: `$ ${money(isSpend ? i.spend : i.revenue)}` })) })
                        : "No data", children: _jsxs(Typography, { sx: { ...FONT.headline, cursor: "default", textAlign: "center" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(actual)] }) }) }), _jsx(Box, { sx: {
                    flex: 1,
                    py: 1.25,
                    px: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    bgcolor: "#fff",
                }, children: _jsx(PerformanceProgressBar, { current: actual, goal: goal, mtdPercent: mtd_goal_percent, goalLabel: `$ ${money(goal)}` }) }), _jsx(HoverTooltip, { title: list?.length ? _jsx(TooltipRows, { heading: isSpend ? "Spend Goal" : "Revenue Goal", items: list.map((i) => ({ name: i.name, value: `$ ${money(i.goal)}` })) }) : "No goal data", children: _jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        width: "100%",
                        borderTop: isSpend ? "1px solid #fee4e2" : "1px solid #d1fadf",
                        py: 0.75,
                        px: 1,
                        bgcolor: isSpend ? "#FEF6FB" : "#E8F9F5",
                        cursor: "default",
                    }, children: [isSpend ? _jsx("img", { src: "/assets/perfgoalicon.svg", alt: "pink coins" }) : _jsx("img", { src: "/assets/revgoalicon.svg", alt: "green coins" }), _jsxs(Typography, { className: "ep-small", sx: {
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#344054",
                                display: "inline-flex",
                                alignItems: "baseline",
                                gap: 0.25,
                                whiteSpace: "nowrap"
                            }, children: ["Goal: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { fontWeight: 600, color: "#181d27" }, children: money(goal) })] })] }) })] }));
});
// ═══════════════ MAIN COMPONENT ═══════════════
export default function EmployeePerformance() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const initialized = useRef(false);
    const rows = useSelector(selectEmployeePerformanceRows);
    const totalSummary = useSelector(selectEmployeePerformanceTotalSummary);
    const pagination = useSelector(selectEmployeePerformancePagination);
    const loading = useSelector(selectEmployeePerformanceLoading);
    const month = useSelector(selectEmployeePerformanceMonth);
    const amType = useSelector(selectEmployeePerformanceAmType);
    const search = useSelector(selectEmployeePerformanceSearch);
    const page = useSelector(selectEmployeePerformancePage);
    const sortBy = useSelector(selectEmployeePerformanceSortBy);
    const sortOrder = useSelector(selectEmployeePerformanceSortOrder);
    const [exporting, setExporting] = useState(false);
    const { permissions, user, token } = useSelector((state) => state.auth);
    // ── Role & Permissions Checks ──
    const isAdmin = user?.role?.role_name?.toLowerCase() === 'admin' || user?.is_superadmin === true;
    const empActions = useMemo(() => {
        const rawActions = permissions?.employee_performance?.actions;
        if (Array.isArray(rawActions))
            return rawActions;
        return [];
    }, [permissions]);
    const hasModuleAccess = isAdmin || permissions?.employee_performance?.access !== false;
    const hasAmPerformanceAccess = isAdmin || empActions.includes('AM Performance');
    // Permitted AM types based on role actions
    // 'Full Service' access grants visibility into all AMs (Paid Media, Affiliate, and Both)
    const isPermissionsLoaded = Boolean(permissions) || isAdmin;
    const hasFullService = isAdmin || !permissions || empActions.includes('Full Service');
    const canViewPaidMedia = hasFullService || empActions.includes('Paid Media');
    const canViewAffiliate = hasFullService || empActions.includes('Affiliate');
    const canViewFullService = hasFullService;
    // Dynamic AM Type dropdown options based on permissions
    const permittedAmTypeOptions = useMemo(() => {
        const opts = [];
        const countPermitted = [canViewPaidMedia, canViewAffiliate, canViewFullService].filter(Boolean).length;
        if (countPermitted > 1 || countPermitted === 0) {
            opts.push({ value: "all", label: "All" });
        }
        if (canViewPaidMedia) {
            opts.push({ value: "paid_media", label: "Paid Media" });
        }
        if (canViewAffiliate) {
            opts.push({ value: "affiliate", label: "Affiliate" });
        }
        if (canViewFullService) {
            opts.push({ value: "both", label: "Full Service" });
        }
        if (opts.length === 0) {
            opts.push({ value: "all", label: "All" });
        }
        return opts;
    }, [canViewPaidMedia, canViewAffiliate, canViewFullService]);
    // Adjust amType if current selection is not in permitted options
    useEffect(() => {
        if (isPermissionsLoaded && permittedAmTypeOptions.length > 0) {
            const isValid = permittedAmTypeOptions.some((o) => o.value === amType);
            if (!isValid) {
                dispatch(setAmType(permittedAmTypeOptions[0].value));
            }
        }
    }, [isPermissionsLoaded, permittedAmTypeOptions, amType, dispatch]);
    const selectedCalendarDate = useMemo(() => toCalendarDate(month), [month]);
    // 1. Filter rows by permissions (Paid Media, Affiliate, Full Service)
    const permittedRows = useMemo(() => {
        if (!permissions && !isAdmin)
            return rows;
        return rows.filter((row) => {
            const rowType = row.type;
            if (rowType === "paid_media")
                return canViewPaidMedia;
            if (rowType === "affiliate")
                return canViewAffiliate;
            if (rowType === "affiliate,paid_media" || rowType === "both")
                return canViewFullService;
            return true;
        });
    }, [rows, permissions, isAdmin, canViewPaidMedia, canViewAffiliate, canViewFullService]);
    // Everything already fits on one page (<=20 total) — sorting can just
    // reorder what's already loaded instead of round-tripping to the backend.
    const totalCount = pagination?.total ?? permittedRows.length;
    const canSortLocally = totalCount <= 20;
    const displayRows = useMemo(() => {
        if (!canSortLocally || !sortBy || !SORT_GETTERS[sortBy])
            return permittedRows;
        const getter = SORT_GETTERS[sortBy];
        const dir = sortOrder === "desc" ? -1 : 1;
        return [...permittedRows].sort((a, b) => {
            const av = getter(a);
            const bv = getter(b);
            if (av < bv)
                return -1 * dir;
            if (av > bv)
                return 1 * dir;
            return 0;
        });
    }, [permittedRows, sortBy, sortOrder, canSortLocally]);
    const showSummaryRow = !!totalSummary && displayRows.length > 0;
    // Permissions arrive after the page opens (always re-fetched from /api/auth/me). Until they do - and for anyone
    // without access - nothing is fetched or shown; the guard below (after all hooks) shows a loader / redirects.
    const accessBlocked = !isPermissionsLoaded || !hasModuleAccess || !hasAmPerformanceAccess;
    // ═══════════════ DATA FETCH ═══════════════
    useEffect(() => {
        if (accessBlocked)
            return;
        if (!initialized.current) {
            initialized.current = true;
            const currentMonth = month || getDefaultApiMonth();
            if (!month)
                dispatch(setMonth(currentMonth));
            dispatch(fetchEmployeePerformance({
                month: currentMonth,
                am_type: amType,
                search: search || undefined,
                page,
                limit: 20,
                sort_by: sortBy || undefined,
                sort_order: sortBy ? sortOrder : undefined,
            }));
            return;
        }
        const delay = search ? 400 : 0;
        const handle = setTimeout(() => {
            dispatch(fetchEmployeePerformance({
                month,
                am_type: amType,
                search: search || undefined,
                page,
                limit: 20,
                sort_by: sortBy || undefined,
                sort_order: sortBy ? sortOrder : undefined,
            }));
        }, delay);
        return () => clearTimeout(handle);
    }, [month, amType, search, page, accessBlocked]);
    // ═══════════════ SORT CHANGE ═══════════════
    // Only hit the backend when there's more than one page of data — otherwise
    // everything needed is already loaded and displayRows sorts it locally.
    useEffect(() => {
        if (accessBlocked || !initialized.current || !month || !sortBy)
            return;
        if (canSortLocally)
            return;
        dispatch(fetchEmployeePerformance({
            month, am_type: amType, search: search || undefined, page, limit: 20,
            sort_by: sortBy, sort_order: sortOrder,
        }));
    }, [sortBy, sortOrder]);
    const handleSortChange = useCallback((columnId, direction) => {
        if (!columnId || !direction)
            return;
        dispatch(setSortConfig({ sortBy: columnId, sortOrder: direction }));
    }, [dispatch]);
    const handleExport = useCallback(async () => {
        // The table only ever holds one page (20 rows) — export needs the full
        // matching set, so it re-requests the same filters with a high limit
        // instead of exporting whatever happens to be on screen.
        if (exporting)
            return;
        setExporting(true);
        try {
            // The token is what narrows the export to the user's client scope, same as the table.
            const response = await ApiCall("POST", "/api/employee-performance/listing", {
                month, am_type: amType, search: search || undefined, page: 1, limit: 9999,
            }, { Authorization: token || "" });
            const responseData = response?.data;
            if (responseData?.success) {
                exportEmployeePerformance(responseData.data.accountManagers, responseData.data.total_summary, month);
            }
            else {
                exportEmployeePerformance([], null, month);
            }
        }
        finally {
            setExporting(false);
        }
    }, [month, amType, search, exporting, token]);
    // ═══════════════ RENDER HELPERS ═══════════════
    const renderCell = useCallback((row, col) => {
        switch (col.id) {
            case "accountManager":
                // A "both" AM shows up under a single-type filter for that side only —
                // the badge should reflect the active filter, not the AM's full type.
                const badgeType = amType !== "all" ? amType : row.type;
                return (_jsxs(Box, { sx: { py: 1.5, width: "100%", display: "flex", flexDirection: "column", gap: 1.25, alignItems: "flex-start", textAlign: "left" }, children: [_jsx(AmBadge, { type: badgeType }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }, onClick: () => navigate(ROUTES.employeePerformanceDetails.path, {
                                state: { userId: row.user_id, name: row.name, month },
                            }), children: [_jsx(Typography, { sx: { ...FONT.headline, fontWeight: 500 }, children: row.name }), _jsx("img", { src: "/assets/link.svg", alt: "link" })] })] }));
            case "accounts":
                return _jsx(AccountsCell, { accounts: row.accounts });
            case "managementFees":
                return _jsx(ManagementFeesCell, { data: row.management_fees, accounts: row.accounts });
            case "commissions":
                return _jsx(CommissionsCell, { data: row.commissions });
            case "placements":
                return _jsx(PlacementsCell, { data: row.placements });
            case "spendPerformance":
                return _jsx(PerformanceCell, { data: row.spend_performance, field: "spend" });
            case "revenuePerformance":
                return _jsx(PerformanceCell, { data: row.revenue_performance, field: "revenue" });
            default:
                return null;
        }
    }, [amType, month, navigate]);
    const renderSummaryRow = useCallback((_rows, col) => {
        if (!totalSummary)
            return null;
        switch (col.id) {
            case "accountManager":
                return (_jsx(Box, { sx: { py: 1, px: 1, display: "flex", alignItems: "center", justifyContent: "flex-start" }, children: _jsx(Typography, { sx: { ...FONT.headline, fontWeight: 700, whiteSpace: "nowrap" }, children: "Total Summary" }) }));
            case "accounts":
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsx(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: Math.round((totalSummary.accounts.total - totalSummary.accounts.lost) * 100) / 100 }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1 }, children: [totalSummary.accounts.new > 0 && (_jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#027a48", display: "inline-flex", alignItems: "center", gap: 0.35, whiteSpace: "nowrap" }, children: [_jsx("span", { style: { fontSize: "10px", color: "#12B76A" }, children: "\u2022" }), " ", totalSummary.accounts.new, " New"] })), totalSummary.accounts.lost > 0 && (_jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#b42318", display: "inline-flex", alignItems: "center", gap: 0.35, whiteSpace: "nowrap" }, children: [_jsx("span", { style: { fontSize: "10px", color: "#F04438" }, children: "\u2022" }), " ", totalSummary.accounts.lost, " Lost"] }))] })] }));
            case "managementFees":
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsxs(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(totalSummary.management_fees.total)] }), _jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#344054", whiteSpace: "nowrap" }, children: ["New: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#344054", fontWeight: 500 }, children: money(totalSummary.management_fees.new) })] })] }));
            case "commissions":
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsxs(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(totalSummary.commissions.total)] }), _jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#344054", whiteSpace: "nowrap" }, children: ["New: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#344054", fontWeight: 500 }, children: money(totalSummary.commissions.new) })] })] }));
            case "placements":
                if (!totalSummary.placements ||
                    totalSummary.placements === "N/A" ||
                    typeof totalSummary.placements !== "object") {
                    return (_jsx(Typography, { sx: { ...FONT.headlineMuted, fontSize: "20px", px: 1, textAlign: "left" }, children: "N/A" }));
                }
                const ttl = totalSummary.placements.total ?? 0;
                const pGoal = totalSummary.placements.goal ?? 0;
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsx(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: `TTL: ${ttl}` }), _jsx(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#344054", whiteSpace: "nowrap" }, children: `Goal: ${pGoal}` })] }));
            case "spendPerformance":
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsxs(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(totalSummary.spend_performance.actual)] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 0.5 }, children: [_jsx("img", { src: "/assets/stats/spend.svg", alt: "spend goal", style: { width: 15, height: 15 } }), _jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#344054", display: "inline-flex", alignItems: "baseline", gap: 0.25, whiteSpace: "nowrap" }, children: ["Goal: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#344054", fontWeight: 500 }, children: money(totalSummary.spend_performance.goal) })] })] })] }));
            case "revenuePerformance":
                return (_jsxs(Box, { sx: { py: 1, px: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 0.5 }, children: [_jsxs(Typography, { sx: { ...FONT.headline, fontSize: "20px", textAlign: "left" }, children: [_jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), money(totalSummary.revenue_performance.actual)] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 0.5 }, children: [_jsx("img", { src: "/assets/coin.svg", alt: "revenue goal", style: { width: 15, height: 15 } }), _jsxs(Typography, { sx: { fontSize: "12px", fontWeight: 500, color: "#344054", display: "inline-flex", alignItems: "baseline", gap: 0.25, whiteSpace: "nowrap" }, children: ["Goal: ", _jsx("span", { style: { color: "#a4a7ae", fontWeight: 500, marginRight: "4px" }, children: "$" }), _jsx("span", { style: { color: "#344054", fontWeight: 500 }, children: money(totalSummary.revenue_performance.goal) })] })] })] }));
            default:
                return null;
        }
    }, [totalSummary]);
    // ── Permission Navigation Guards ──
    // Kept below every hook: an early return above them changes the hook count between renders.
    // Permissions not here yet: a blank page with a loader, never the listing (it may redirect a moment later).
    if (!isPermissionsLoaded) {
        return (_jsx(Box, { sx: { position: "relative", width: "100%", minHeight: "calc(100vh - 120px)", bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    if (isPermissionsLoaded) {
        if (!hasModuleAccess) {
            return _jsx(Navigate, { to: ROUTES.notFound.path, replace: true });
        }
        // No "AM Performance" action -> no listing of everyone; the person lands on their own details page.
        if (!hasAmPerformanceAccess) {
            const selfId = String(user?.id ?? user?._id ?? "");
            if (!selfId)
                return _jsx(Navigate, { to: ROUTES.notFound.path, replace: true });
            const selfName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
            return (_jsx(Navigate, { to: ROUTES.employeePerformanceDetails.path, replace: true, state: { userId: selfId, ownerId: selfId, name: selfName, month: month || getDefaultApiMonth() } }));
        }
    }
    return (_jsx(PageContainer, { children: _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: _jsxs(Box, { sx: { bgcolor: "#fff", borderRadius: "8px", border: "1px solid #EAECF0", overflow: "hidden", position: "relative" }, children: [_jsx(Box, { sx: { px: 1, borderBottom: "1px solid #EAECF0" }, children: _jsx(TableToolbar, { title: "", showSearch: true, showFullScreen: true, searchValue: search, onSearchChange: (v) => dispatch(setSearch(v)), searchPlaceholder: "Search account managers...", customActionsLeft: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Dropdown, { value: amType, onChange: (v) => dispatch(setAmType(v)), options: permittedAmTypeOptions, placeholder: "AM Type: All", displayValue: renderDropdownValue("AM Type", truncateName(permittedAmTypeOptions.find((o) => o.value === amType)?.label || "All", 12)), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, className: "header-dropdown" }), _jsx(MonthPicker, { showAllTime: false, value: selectedCalendarDate, onChange: (v) => v !== "ALL_TIME" && dispatch(setMonth(toApiMonth(`${v.year}-${String(v.month).padStart(2, "0")}`))), fullWidth: false, className: "header-dropdown" }), _jsx(ExportLoadingButton, { loading: exporting, onClick: handleExport })] }) }) }), _jsx(Box, { sx: {
                            '& .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell': {
                                border: "1px solid #E9EAEB !important",
                            },
                            // GlobalFontSizes.css forces `.reusable-table tbody tr td *` to the body size, which
                            // beats sx fontSize. This is more specific, so any text tagged "ep-small" (and its
                            // inner spans) takes this size. Change the one value here to resize them all.
                            '& .reusable-table tbody tr td .ep-small, & .reusable-table tbody tr td .ep-small span': {
                                fontSize: "13px",
                            },
                            '& .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="accounts"], & .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="managementFees"], & .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="commissions"], & .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="placements"], & .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="spendPerformance"], & .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell[data-column-id="revenuePerformance"]': {
                                padding: "0px !important",
                                verticalAlign: "top",
                            },
                            '& .reusable-table-container .reusable-table .reusable-table-footer .reusable-table-row .reusable-table-cell, & .reusable-table-container .reusable-table tfoot .reusable-table-cell': {
                                padding: "8px 4px !important",
                                verticalAlign: "middle",
                                backgroundColor: "#F5F5F5 !important",
                            }
                        }, children: _jsx(CommonTable, { columns: columns, rows: displayRows, renderCell: renderCell, isFirstLoad: loading && !rows.length, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 155px)", expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: sortBy ? { columnId: sortBy, direction: sortOrder } : null, onSortChange: handleSortChange, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, pinnedColumns: PINNED_COLUMNS, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM }) }), loading && (_jsx(Box, { sx: {
                            position: "absolute",
                            inset: 0,
                            background: displayRows.length > 0 ? "rgba(255,255,255,0.7)" : "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 100,
                        }, children: _jsx(CommonLoader, { className: "table-loader" }) }))] }) }) }));
}
